import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, TriangleAlert } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../components/ui/select";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Criar e editar campanha.
//
// O objetivo é escolhido em português e pelo que a pessoa quer conseguir, não
// pela sigla da plataforma ("OUTCOME_ENGAGEMENT"). A campanha nasce pausada
// de propósito: subir gastando antes de alguém conferir o criativo é dinheiro
// queimado, e a Meta começa a cobrar no minuto seguinte.

const OBJETIVOS = [
  {
    valor: "engagement",
    rotulo: "Conversas no WhatsApp",
    dica: "Quem clicar cai no atendimento. É o que alimenta a tela de Tickets."
  },
  {
    valor: "lead_generation",
    rotulo: "Cadastro de leads",
    dica: "Formulário dentro da própria rede, sem sair para o site."
  },
  {
    valor: "traffic",
    rotulo: "Visitas ao site",
    dica: "Leva para um endereço fora da rede."
  },
  {
    valor: "awareness",
    rotulo: "Alcance",
    dica: "Mostrar para o máximo de gente. Não otimiza por resposta."
  },
  {
    valor: "video_views",
    rotulo: "Visualizações de vídeo",
    dica: "Para quem tem um vídeo e quer que ele seja assistido."
  },
  {
    valor: "conversions",
    rotulo: "Conversões no site",
    dica: "Exige pixel instalado e evento configurado."
  }
];

const CampanhaModal = ({ open, campanha, onClose, onSalvo }) => {
  const editando = Boolean(campanha);

  const [nome, setNome] = useState("");
  const [objetivo, setObjetivo] = useState("engagement");
  const [conta, setConta] = useState("");
  const [orcamento, setOrcamento] = useState("");
  const [tipoOrcamento, setTipoOrcamento] = useState("daily");
  const [ativa, setAtiva] = useState(false);

  const [destinos, setDestinos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) return;

    setNome(campanha?.campaignName || "");
    setObjetivo("engagement");
    setOrcamento(
      campanha?.budget?.amount ? String(campanha.budget.amount) : ""
    );
    setTipoOrcamento(campanha?.budget?.type || "daily");
    setAtiva(false);

    if (editando) return;

    // Onde criar só importa numa campanha nova: na edição a conta já está
    // decidida e a plataforma não deixa mudar.
    (async () => {
      setCarregando(true);

      try {
        const { data } = await api.get("/zernio/ads/accounts");

        setDestinos(data.adAccounts || []);
        setConta(data.adAccounts?.[0]?.adAccountId || "");
      } catch (err) {
        toastError(err);
      }

      setCarregando(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campanha]);

  const salvar = async () => {
    if (!nome.trim()) {
      toast.error("Dê um nome à campanha.");
      return;
    }

    setSalvando(true);

    try {
      if (editando) {
        await api.put(
          `/zernio/ads/campaigns/${campanha.platformCampaignId}`,
          {
            platform: campanha.platform,
            name: nome.trim(),
            ...(orcamento
              ? { budgetAmount: Number(orcamento), budgetType: tipoOrcamento }
              : {}),
            accountId: campanha.accountId
          }
        );

        toast.success("Campanha atualizada.");
      } else {
        const destino = destinos.find((item) => item.adAccountId === conta);

        if (!destino) {
          toast.error("Escolha a conta de anúncios.");
          setSalvando(false);
          return;
        }

        await api.post("/zernio/ads/campaigns", {
          accountId: destino.accountId,
          adAccountId: destino.adAccountId,
          name: nome.trim(),
          goal: objetivo,
          ...(orcamento
            ? { budgetAmount: Number(orcamento), budgetType: tipoOrcamento }
            : {}),
          ativa
        });

        toast.success(
          ativa
            ? "Campanha criada e ativa."
            : "Campanha criada, pausada. Ative quando os anúncios estiverem prontos."
        );
      }

      await onSalvo();
      onClose();
    } catch (err) {
      toastError(err);
    }

    setSalvando(false);
  };

  const escolhido = OBJETIVOS.find((item) => item.valor === objetivo);
  const moeda = destinos.find((item) => item.adAccountId === conta)?.moeda;

  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editando ? "Editar campanha" : "Nova campanha"}
          </DialogTitle>
          <DialogDescription>
            {editando
              ? "Nome e orçamento. O objetivo não muda depois que a campanha existe."
              : "A campanha nasce sem anúncios: crie os criativos no painel da rede depois."}
          </DialogDescription>
        </DialogHeader>

        {carregando ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="campanha-nome">Nome</Label>
              <Input
                id="campanha-nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: Imóveis — outubro"
              />
            </div>

            {!editando && (
              <>
                <div className="space-y-1.5">
                  <Label>Conta de anúncios</Label>
                  {destinos.length === 0 ? (
                    <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs">
                      Nenhuma conta de anúncios encontrada. Conecte uma no
                      painel da Zernio antes de criar campanha aqui.
                    </p>
                  ) : (
                    <Select value={conta} onValueChange={setConta}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {destinos.map((item) => (
                          <SelectItem
                            key={item.adAccountId}
                            value={item.adAccountId}
                          >
                            {item.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label>Objetivo</Label>
                  <Select value={objetivo} onValueChange={setObjetivo}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OBJETIVOS.map((item) => (
                        <SelectItem key={item.valor} value={item.valor}>
                          {item.rotulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {escolhido && (
                    <p className="text-xs text-muted-foreground">
                      {escolhido.dica}
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="campanha-orcamento">Orçamento</Label>
              <div className="flex gap-2">
                <Input
                  id="campanha-orcamento"
                  type="number"
                  min="1"
                  step="0.01"
                  className="flex-1"
                  value={orcamento}
                  onChange={(e) => setOrcamento(e.target.value)}
                  placeholder="opcional"
                />
                <Select value={tipoOrcamento} onValueChange={setTipoOrcamento}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">por dia</SelectItem>
                    <SelectItem value="lifetime">no total</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                Valor cheio na moeda da conta{moeda ? ` (${moeda})` : ""}: 31 é
                trinta e um, não trinta e um centavos. Deixe vazio para definir
                o orçamento em cada conjunto de anúncios.
              </p>
            </div>

            {!editando && (
              <label className="flex cursor-pointer items-start gap-2 rounded-lg border p-3">
                <Checkbox
                  className="mt-0.5"
                  checked={ativa}
                  onCheckedChange={(valor) => setAtiva(Boolean(valor))}
                />
                <span>
                  <span className="text-sm font-medium">
                    Já subir ativa
                  </span>
                  <span className="mt-0.5 flex items-start gap-1 text-xs text-amber-600 dark:text-amber-400">
                    <TriangleAlert className="mt-0.5 h-3 w-3 shrink-0" />
                    Começa a gastar assim que houver anúncio aprovado. Deixe
                    desmarcado para conferir antes.
                  </span>
                </span>
              </label>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            onClick={salvar}
            disabled={salvando || carregando || (!editando && !conta)}
          >
            {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
            {editando ? "Salvar" : "Criar campanha"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CampanhaModal;
